import {
  type SanityCombinedListingConfiguration,
  type SanityCombinedListingProductFragment,
} from '@data/sanity/queries/types/product'
import { isNumeric } from '@lib/helpers'
import { getSanityImageUrl } from '@lib/image'
import {
  type OptionalNumber,
  type OptionalSize,
  getIsSmallerThan,
} from '@lib/size'
import { findCombinedListingProducts } from './product'
import { filterProductsBySize } from './size'
import { type ExtendedDropdownOption } from './types'

/**
 * Gets dropdown input options from products.
 */
export const getCombinedListingProductDropdownOptions = (
  products: SanityCombinedListingProductFragment[],
  size: string | null,
  noSelectionTitle?: string,
) => {
  const productOptions: ExtendedDropdownOption[] = []

  if (noSelectionTitle) {
    productOptions.push({
      value: '',
      title: noSelectionTitle,
    })
  }

  products.filter(filterProductsBySize(size)).forEach((product) => {
    let imageUrl: string | undefined

    if (product.combinedListingProductType === 'frame') {
      // Add image URL from frame builder texture
      const textureImage =
        product.combinedListingFrameBuilderProfile?.texture?.icon ??
        product.combinedListingFrameBuilderProfile?.texture?.image
      imageUrl = getSanityImageUrl(textureImage, {
        width: 32,
        height: 32,
      })
    }

    productOptions.push({
      value: `${product.productID}`,
      title: product.title,
      imageUrl,
    })
  })

  return productOptions
}

/**
 * Determines whether combined listing consists of an empty frame product.
 */
export const getIsEmptyFrame = (
  combinedListingProducts: SanityCombinedListingProductFragment[],
) => {
  const frameId = combinedListingProducts.find(
    (product) => product.combinedListingProductType === 'frame',
  )?.productID
  const glassId = combinedListingProducts.find(
    (product) => product.combinedListingProductType === 'glass',
  )?.productID
  const backboardId = combinedListingProducts.find(
    (product) => product.combinedListingProductType === 'backboard',
  )?.productID
  const passepartoutId = combinedListingProducts.find(
    (product) => product.combinedListingProductType === 'passepartout',
  )?.productID
  const spacerId = combinedListingProducts.find(
    (product) => product.combinedListingProductType === 'spacer',
  )?.productID

  return (
    combinedListingProducts.length === 1 &&
    !!frameId &&
    !glassId &&
    !backboardId &&
    !passepartoutId &&
    !spacerId
  )
}

/**
 * Converts a size string into a number.
 */
const getSizeNumber = (size: string) => {
  const sizeString = size.replaceAll(',', '.')
  const sizeNumber: OptionalNumber = isNumeric(sizeString)
    ? Number(sizeString)
    : undefined

  return sizeNumber
}

/**
 * Determines whether combined listing product size is large format.
 */
export const getIsCombinedListingLargeFormat = (
  combinedListingConfiguration: SanityCombinedListingConfiguration,
  productIds: number[],
  size: string,
) => {
  // Get combined listing dimensions from size string
  const sizeParts = size.split('x')
  const frameSize = [
    getSizeNumber(sizeParts[0]),
    getSizeNumber(sizeParts[1]),
  ] as OptionalSize

  if (
    typeof frameSize[0] === 'undefined' ||
    typeof frameSize[1] === 'undefined'
  ) {
    return false
  }

  // Go through each combined listing product
  const products = findCombinedListingProducts(
    combinedListingConfiguration,
    productIds,
  )

  return products.reduce((isLargeFormat, product) => {
    if (isLargeFormat || !product.combinedListingLargeFormatSettings) {
      return isLargeFormat
    }

    // Get large format settings from glass and compare with frame size
    const { dimentsion1, dimentsion2 } =
      product.combinedListingLargeFormatSettings
    const isSmallerThan = getIsSmallerThan(frameSize, [
      dimentsion1,
      dimentsion2,
    ])

    return !isSmallerThan
  }, false)
}
